import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Injector, Input, OnChanges, OnDestroy, OnInit, Output, PipeTransform, SimpleChanges, TemplateRef, TrackByFunction, Type, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { SessionStorageService } from '@services/storage.service';
import { GenericTableColumn } from '@shared/models/generictablecolumn';
import { SubMenuHandlerService } from '@shared/services/subMenuHandler.service';
import { SelectSubMenuItem, SelectSubMenuItemManager } from '@shared/utils/SelectSubMenuItemManager';
import { ITableFilter, ModelfilterPredicate } from '@shared/utils/TableDataSource';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { SelectActionManager } from '../../utils/SelectActionManager';
import { SelectManager } from '../../utils/SelectManager';
import { actionDefintion, SimpleActionManager } from '../../utils/SimpleActionManager';
import { type FilterPredicate, filterPredicateFor, type SortingDataAccessor, sortingDataAccessorFor } from './generictable-sorting';

/**
 * What each data source sorted and filtered by before a table took it over, by the data source.
 *
 * At module level rather than on the table, because a host whose table stands in an `*ngIf` builds a new table over the same data source every time it reappears. A new table has to take the data source over again — its closures must belong to the live table — but from the accessor and predicate the host set, not from
 * the wrapper the table before it left behind, or the wrappers would nest once per reappearance and keep every dead table alive.
 */
const inheritedSorting = new WeakMap<object, SortingDataAccessor>();
const inheritedFiltering = new WeakMap<object, FilterPredicate>();

@Component({
	selector: 'app-generictable',
	templateUrl: './generictable.component.html',
	styleUrls: ['./generictable.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class GenerictableComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	public columnsToDisplay: string[];
	public colCount: number;
	public dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

	// MatPaginator Output
	public pageEvent: PageEvent;

	@ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
	@ViewChild(MatSort, { static: false }) sort: MatSort;
	@ViewChild(MatTable, { static: false }) private matTable?: MatTable<any>;

	@Input() public selected: string[] = [];
	@Input() public tableColumns: GenericTableColumn[];
	@Input() public hasSelectColumn: boolean = false;
	@Input() public pageSizes: number[] = [5, 10, 20, 100];
	@Input() public initialPageSize: number = 20;
	@Input() public showFirstLastButtons: boolean = true;
	@Input() public rowActions: any[] | actionDefintion<any>[];
	@Input() public selectActions: actionDefintion<any>[] | SelectSubMenuItem<any>[];

	@Input() public totalRowCount: number;
	@Input() public showPaginator: boolean = true;
	@Input() public resetSelectionEvent?: EventEmitter<void>;

	/**
	 * Optional expandable-row support. When set, an expand/collapse toggle column is rendered first and the provided template is shown as a full-width detail row for the currently expanded row. Single-expand: the parent owns `expandedRow` state and updates it in response to `rowExpandToggle`. When unset, the table
	 * behaves exactly as before (no toggle column, no detail rows, multiTemplateDataRows off).
	 */
	@Input() public expandableDetailTemplate: TemplateRef<any> | null = null;
	@Input() public expandedRow: any = null;
	@Output() public rowExpandToggle = new EventEmitter<any>();
	/** Optional per-row toggle state for a parent that keeps several rows open itself: `true` open, `false` closed, `null` no toggle on that row. When unset, the toggle shows `expandedRow === row` as before. */
	@Input() public rowExpanded: ((row: any) => boolean | null) | null = null;
	public trackByRow: TrackByFunction<any> = (_index, row) => row?.self ?? row?.id ?? row;

	/** Optional per-row CSS class hook. Returns class name(s) applied to each data `mat-row`, letting a consumer colour/highlight rows (e.g. by status). When unset, no class is added and rows render exactly as before. Consumers style the returned classes via `::ng-deep`. */
	@Input() public rowClass: ((row: any) => string | string[]) | null = null;

	private destroyNotifier$ = new Subject<void>();

	// Filtering from parent component
	private _filterWord: string | ITableFilter;
	@Input()
	get filterWord() {
		return this._filterWord;
	}

	set filterWord(value: string | ITableFilter) {
		this._filterWord = value;

		this.setFilterOnDataSource();

		// If the dataSource is filtered, we need to set the data for the selectManager
		// so the select all only selects the filtered data while maintaining the selections
		this.selectManager?.setData(this.dataSource.filteredData, false); // <-- Set data here
	}

	private setFilterOnDataSource() {
		// If the filter is already an object, we cannot set it to a string
		// the the filterPredicate will not work as expected
		if (typeof this._filterWord === 'string' && typeof this._filterWord === 'object') {
			throw new TypeError('Cannot set filterWord as a string when it is already an object');
		}

		// If the filter is an object, we need to use a custom filter predicate
		// If the filter is a string, we can use the default filter predicate
		if (typeof this._filterWord === 'object') {
			this.dataSource.filterPredicate = ModelfilterPredicate(); // <-- Set filterPredicate here
		}

		this.dataSource.filter = this._filterWord as string; // <-- Set filter here
	}

	private _tableData: any;
	@Input()
	get tableData() {
		return this._tableData;
	}

	set tableData(value: any) {
		/* just before setting the tableData value,
		 * Angular sets the value to null and then the actual data.
		 *
		 * It seems to be part of the template update binding engine lifecycle.
		 *
		 * To prevent null being passed in and resetting everything,
		 * we return so mat-table will work correctly when replacing
		 * the underlying data source, for example page index,
		 * if the items length is within range of the current mat-paginator index.
		 */
		if (value === null) {
			return;
		}

		this._tableData = value;
		this.dataSource.data = this._tableData;
		// If the dataSource is set, we need to set the data for the selectManager
		this.selectManager?.setData(this.dataSource.data); // <-- Set data here
	}

	private _tableSource: any;
	/** The tableSource is the data source for the table. It can be set to a MatTableDataSource or any other data source. If it is set, the dataSource will be set to the tableSource. If the tableSource is set, we need to use tableData$ for selections with filtering to work properbly. */
	@Input()
	get tableSource() {
		return this._tableSource;
	}

	private dataSourceSubscription: Subscription;
	set tableSource(value: any) {
		this._tableSource = value;
		this.dataSource = this._tableSource;
		this.adoptSorting(this.dataSource);

		// If the filterWord is set, we need to set the filter for the new dataSource
		if (this.filterWord) {
			this.setFilterOnDataSource();
		}

		// If there is an existing subscription, we need to unsubscribe from it
		if (this.dataSourceSubscription) this.dataSourceSubscription.unsubscribe();

		if (this.selectManager) {
			// If the tableSource is set, we need to set the data for the selectManager and listen to changes
			this.setupSubscriptions();
		}
	}

	private setupSubscriptions(): void {
		this.dataSourceSubscription?.unsubscribe();

		this.dataSourceSubscription = this.dataSource.connect().subscribe((_) => {
			// if the dataSource Data changes, we need to reset the selectManager data
			if (this.getDataChanged()) {
				this.selectManager.setData(this.dataSource.filteredData, true); // <-- Set data here
				this.dataCount = this.dataSource.data.length;
			}
		});
	}

	private dataCount = 0;
	// THIS COULD BE MADE BETTER - currently only checks if the length of the data has changed
	private getDataChanged(): boolean {
		return this.dataCount !== this.dataSource.data.length;
	}

	public selectManager: SelectManager<any>;
	public selectActionManager: SelectActionManager<any>;
	public SubMenuSelectManager: SelectSubMenuItemManager<any>;
	public itemActionManager: SimpleActionManager<any>;

	@Output() selectedList = new EventEmitter<object[]>();
	@Output() filterCriteria = new EventEmitter<string>();
	@Output() menuEmitter = new EventEmitter<object>();
	@Output() paginatorEmitter = new EventEmitter<object>();

	constructor(
		private sessionStorageService: SessionStorageService,
		private translateService: TranslateService,
		private headerSubMenuService: SubMenuHandlerService,
		private injector: Injector,
	) {
		this.adoptSorting(this.dataSource);
	}

	/**
	 * Makes a data source sort its columns by the `sortKey` a column names or by what the column shows — see `sortingDataAccessorFor` — and keep the values that are only there to sort by out of its free-text filter — see `filterPredicateFor`.
	 *
	 * On the table's own data source from the start, and on every one a host hands over through `tableSource`, since the host's replaces the table's. What the data source did before is kept as the fallback: Material's default for a plain column, and whatever accessor or predicate the host set itself before handing it
	 * over. Taken over again by every table built over the same data source, from that same fallback — see {@link inheritedSorting}.
	 */
	private adoptSorting(dataSource: MatTableDataSource<any>): void {
		if (!dataSource) {
			return;
		}
		const sorting = inheritedSorting.get(dataSource) ?? dataSource.sortingDataAccessor;
		inheritedSorting.set(dataSource, sorting);
		const columns = (): ReadonlyArray<GenericTableColumn> => this.tableColumns ?? [];
		dataSource.sortingDataAccessor = sortingDataAccessorFor({
			columns,
			inherited: sorting,
			pipeFor: (pipe) => this.pipeFor(pipe),
			translate: (key) => this.translateService.instant(key),
		});
		const filtering = inheritedFiltering.get(dataSource) ?? dataSource.filterPredicate;
		inheritedFiltering.set(dataSource, filtering);
		dataSource.filterPredicate = filterPredicateFor(columns, filtering);
	}

	/** The instance of a column's pipe, found the way `dynamicPipe` finds it for the cells: from the injector, or made without arguments when the injector has none. */
	private pipeFor(pipe: Type<PipeTransform>): PipeTransform {
		return this.injector.get(pipe, null) ?? new pipe();
	}

	ngOnChanges(changes: SimpleChanges): void {
		// The expandable detail row uses a `when` predicate (isExpandedRow), which mat-table only
		// re-evaluates during renderRows(). A change to `expandedRow` (owned by the parent) is
		// external state, so without forcing a re-render the detail row never toggles open/closed.
		if (changes['expandedRow'] && !changes['expandedRow'].firstChange) {
			this.matTable?.renderRows();
		}
	}

	ngOnInit(): void {
		// Extract column names
		this.colCount = this.tableColumns.length;
		this.columnsToDisplay = this.tableColumns.map((tableColumn: GenericTableColumn) => tableColumn.dataKey);

		if (this.selectActions) {
			this.hasSelectColumn = true;
		}

		// Display select-column if activated
		if (this.hasSelectColumn) {
			this.columnsToDisplay.unshift('select');
			// Initialize selectManager
			this.selectManager = new SelectManager<any>();
			this.selectManager.setData(this.dataSource.filteredData);

			this.selectManager.selectedList.pipe(takeUntil(this.destroyNotifier$)).subscribe((item) => {
				this.selectedList.emit(item);
			});

			if (this.tableSource) {
				this.setupSubscriptions();
			}
		}

		// Matches the `*ngIf="rowActions"` guard on the `rowMenu` matColumnDef in the template —
		// listing 'rowMenu' here unconditionally while its column definition is conditional causes
		// Angular Material's "Could not find column" error whenever a consumer sets rowActions falsy.
		if (this.rowActions) {
			this.columnsToDisplay.push('rowMenu');
		}

		// Expandable rows: prepend the toggle column so it is the first column.
		if (this.expandableDetailTemplate) {
			this.columnsToDisplay.unshift('expand');
		}

		if (this.selectActions && this.selectActions.length) {
			if (this.isActionDefintionArray(this.selectActions)) {
				this.selectActionManager = new SelectActionManager<any>();
				this.selectActionManager.setSelectManager(this.selectManager);
				this.selectActionManager.setActions(this.selectActions);
			} else {
				this.SubMenuSelectManager = new SelectSubMenuItemManager<any>();
				this.SubMenuSelectManager.setSelectManager(this.selectManager);
				this.SubMenuSelectManager.setActions(this.selectActions);

				const MenuItemKey = 'generic-table';
				this.headerSubMenuService.setMenuItem(MenuItemKey, this.SubMenuSelectManager.getSubMenuItems());
				this.destroyNotifier$.subscribe(() => {
					this.headerSubMenuService.removeMenuItems(MenuItemKey);
				});
			}
		}

		if (this.rowActions) {
			this.itemActionManager = new SimpleActionManager<any>();
			this.itemActionManager.setActions(this.rowActions);
		}
	}

	isActionDefintionArray(arr: actionDefintion<any>[] | SelectSubMenuItem<any>[]): arr is actionDefintion<any>[] {
		return arr && arr.length > 0 && 'title' in arr[0];
	}

	ngAfterViewInit() {
		// Ugly ExpressionChangedAfterItHasBeenCheckedError hack
		// https://stackoverflow.com/questions/53013602/matsort-and-matpaginator-does-not-work-without-settimeout
		setTimeout(() => {
			this.dataSource.sort = this.sort;
			if (this.paginator) {
				this.dataSource.paginator = this.paginator;

				if (this.totalRowCount !== undefined) {
					this.dataSource.paginator.length = this.totalRowCount;
				}
			}
		}, 500);

		this.sort.sortChange.pipe(takeUntil(this.destroyNotifier$)).subscribe(() => {
			if (!this.paginator) {
				return;
			}
			this.paginator.firstPage();
			this.paginatorEmitter.emit({
				pageNumber: this.paginator.pageIndex,
				pageSize: this.paginator.pageSize,
				lastPage: !this.paginator.hasNextPage(),
				firstPage: !this.paginator.hasPreviousPage(),
			});
		});

		if (this.resetSelectionEvent) {
			this.resetSelectionEvent.pipe(takeUntil(this.destroyNotifier$)).subscribe(() => {
				this.selectManager.resetSelection();
			});
		}
	}

	ngOnDestroy(): void {
		this.destroyNotifier$.next();
		this.destroyNotifier$.complete();
	}

	emitToParent(actionName, rowData) {
		this.menuEmitter.emit({ action: actionName, data: rowData });
	}

	/** Emits the row whose expand toggle was clicked; the parent owns `expandedRow` state. */
	toggleRowExpand(row: any): void {
		this.rowExpandToggle.emit(row);
	}

	/** `matRowDef` predicate for the detail row — renders the expandable detail only for the currently expanded row (single-expand). Always false when no detail template is set, so the table renders identically to before when expansion is unused. */
	isExpandedRow = (_index: number, row: any): boolean => this.expandableDetailTemplate != null && this.expandedRow === row;

	emitSelectAction(action: actionDefintion<any>) {
		this.menuEmitter.emit({
			action,
			data: this.selectManager.getSelectedItems(),
		});
	}

	// emitFilterCriteria(criteria){ /* if the filter input is placed internally in this component, this could emit filter criteria to the parent data source */
	//   console.log(this.currentFilterCriteria);
	//   this.filterCriteria.emit(this.currentFilterCriteria)
	// }

	numForm(val: number, currency: any) {
		if (currency && currency.length !== 3) return val;
		return new Intl.NumberFormat(this.sessionStorageService.get('User.PreferredLanguage') ? this.sessionStorageService.get('User.PreferredLanguage') : 'da', currency ? { style: 'currency', currency } : { style: 'decimal' }).format(val);
	}

	// Hent "dyb" property baseret på en key leveret som en streng, f.eks. "item.id"
	getDescendantProp(row: object, descendant: string) {
		const descendantArray = descendant.split('.');
		while (descendantArray.length) {
			const property = descendantArray.shift();
			// The row could possibly be undefined, so we check here
			if (row) {
				row = row[property];
			} else {
				return '';
			}
		}
		return row;
	}

	getColValueFromRow(row, col: GenericTableColumn): any {
		let value = row[col.dataKey];

		if (col.dataKey.includes('.')) {
			return this.getDescendantProp(row, col.dataKey);
		}

		value ??= col.defaultValue;

		return value;
	}

	parsedValue(row, col: GenericTableColumn) {
		let val = this.getColValueFromRow(row, col);

		if (col.isStatusIcon) {
			if (val === 'acies_true' || val === 'acies_false' || val === 'acies_pending') {
				return val;
			}
			if (val === 'Succeeded') {
				val = 'acies_true';
			}
			if (val === 'Failed') {
				val = 'acies_false';
			}
			if (val === 'Pending') {
				val = 'acies_pending';
			}
		}

		if (col.numberFormat) {
			val = this.numForm(val, 'currency' in row ? row['currency'] : false);
		}

		if (col.url) {
			const urlPattern = col.url.split('**')[1];
			val = `<a href='${col.url.replace(`**${urlPattern}`, row[urlPattern])}'>${val}</a>`;
		}

		if (col.isImg) {
			val = `<img src='${val}'>`;
		}

		if (col.isLink) {
			const rel = col.linkOpener ? " rel='opener'" : '';
			val = `<a href='${val}' target='_blank'${rel}>${this.translateService.instant(col.linkTitle)}</a>`;
		}

		if (col.isTranslatable) {
			val = this.translateService.instant(val);
		}

		return val;
	}
}
